import React, { useState } from 'react';
import {
  formatAnswerLabel,
  formatReadingScorePercent,
} from './readingComprehensionReview';
import './ReadingComprehensionResults.css';

export default function ReadingComprehensionResults({
  review,
  score,
  totalQuestions,
  examType = 'GRE',
  onReviewQuestions,
  onExit,
  children,
}) {
  const [expandedId, setExpandedId] = useState(null);
  const questions = review?.questions || [];
  const missed = questions.filter((question) => question.is_correct !== true);
  const listedQuestions = missed.length ? missed : questions;
  const correctCount = review?.correct_count ?? score;
  const total = review?.total_questions ?? totalQuestions;
  const percent = formatReadingScorePercent(
    correctCount,
    total,
    review?.score_percent
  );

  return (
    <div className="result-box rc-results">
      <div className="rc-results-header">
        <h2>Test Completed!</h2>
        <div className="rc-results-score">
          Your score: <strong>{correctCount} / {total}</strong> ({percent}%)
        </div>
        {review?.topic || review?.difficulty ? (
          <div className="rc-results-meta">
            {[review.topic, review.difficulty].filter(Boolean).join(' · ')}
          </div>
        ) : null}
      </div>

      {review?.skill_breakdown?.length > 0 && (
        <div className="rc-skill-grid">
          {review.skill_breakdown.map((skill) => (
            <div className="rc-skill-card" key={skill.skill}>
              <strong>{skill.label}</strong>
              <span>
                {skill.correct}/{skill.attempted} · {skill.accuracy}%
              </span>
            </div>
          ))}
        </div>
      )}

      {(review?.weaknesses?.length || review?.strengths?.length) && (
        <div className="rc-callout">
          {review.weaknesses?.length > 0 && (
            <p>
              <strong>Rebuild: </strong>
              {review.weaknesses.join(', ')}
            </p>
          )}
          {review.strengths?.length > 0 && (
            <p>
              <strong>Holding up: </strong>
              {review.strengths.join(', ')}
            </p>
          )}
        </div>
      )}

      {listedQuestions.length > 0 && (
        <div className="rc-question-review">
          <h3>
            {missed.length
              ? examType === 'TAGE'
                ? 'Questions manquées'
                : 'Where your errors are'
              : examType === 'TAGE'
                ? 'Détail des questions'
                : 'Question breakdown'}
          </h3>
          {listedQuestions.map((question) => {
            const key = question.question_id ?? question.order_in_passage;
            const open = expandedId === key;
            return (
              <div className="rc-question-item" key={key}>
                <button
                  type="button"
                  className="rc-question-toggle"
                  onClick={() => setExpandedId(open ? null : key)}
                >
                  <div className="rc-question-toggle-main">
                    <span className={question.is_correct === true ? 'rc-pill rc-pill-ok' : 'rc-pill rc-pill-miss'}>
                      {question.is_correct === true
                        ? 'Correct'
                        : question.is_correct === false
                          ? examType === 'TAGE'
                            ? 'Manquée'
                            : 'Missed'
                          : examType === 'TAGE'
                            ? 'Non répondue'
                            : 'Unanswered'}{' '}
                      · Q{question.order_in_passage ?? '?'}
                    </span>
                    <span className="rc-pill rc-pill-skill">
                      {question.gre_skill_label}
                    </span>
                    <strong>{question.question_text}</strong>
                  </div>
                  <span>{open ? '−' : '+'}</span>
                </button>
                {open && (
                  <div className="rc-question-body">
                    <p>
                      <strong>Your answer: </strong>
                      {formatAnswerLabel(
                        question.user_answer_display ?? question.user_answer,
                        question.options
                      )}
                    </p>
                    <p>
                      <strong>Correct answer: </strong>
                      {formatAnswerLabel(
                        question.correct_answers_display ?? question.correct_answers,
                        question.options
                      )}
                    </p>
                    {question.explanation ? (
                      <p>
                        <strong>Why: </strong>
                        {question.explanation}
                      </p>
                    ) : null}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {review?.coaching_notes?.length > 0 && (
        <div className="rc-callout">
          <h3>{examType === 'TAGE' ? 'Prochaine étape' : 'Next GRE move'}</h3>
          <ul>
            {review.coaching_notes.map((note) => (
              <li key={note}>{note}</li>
            ))}
          </ul>
        </div>
      )}

      {children}

      <div className="rc-results-actions">
        {onReviewQuestions && (
          <button type="button" className="rc-secondary" onClick={onReviewQuestions}>
            {examType === 'TAGE' ? 'Revoir chaque question' : 'Review each question'}
          </button>
        )}
        <button type="button" className="rc-primary" onClick={onExit}>
          Return to {examType === 'TAGE' ? 'TAGE MAGE' : 'Reading Comprehension'}
        </button>
      </div>
    </div>
  );
}
